// Instanced renderer for roads and bridges — flat planes laid on terrain.
// Roads use per-owner tinting so multi-player road networks read as distinct.

import type { SceneNode } from '@aod/protocol';
import { useEffect, useRef } from 'react';
import type * as THREE from 'three';
import {
  type InfraEntry,
  _color,
  _dummy,
  bridgeTint,
  roadGeo,
  roadTintFor,
} from './InfrastructureMaterials.js';
import { RenderLayer } from './RenderLayer.js';
import { tileElevationAt } from './elevation.js';

interface InfraInstancedProps {
  readonly entries: ReadonlyArray<InfraEntry>;
  readonly material: THREE.MeshStandardMaterial;
  readonly yOffset: number;
  readonly biomeMap: Map<string, string>;
  readonly perOwnerTint?: boolean;
  /** Path-material tint (dominion-path M5): colour from the tile's
   * controller archetype/alignment instead of the plain material. */
  readonly pathTint?: boolean;
}

export function InfraInstanced({
  entries,
  material,
  yOffset,
  biomeMap,
  perOwnerTint,
  pathTint,
}: InfraInstancedProps) {
  const meshRef = useRef<THREE.InstancedMesh>(null);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh || entries.length === 0) return;

    const dummy = _dummy;
    const color = _color;
    entries.forEach(({ tile }, localIdx) => {
      const [x, , z] = tile.transform.position;
      const props = tile.props as Record<string, unknown> | undefined;
      const q = (props?.q as number) ?? 0;
      const r = (props?.r as number) ?? 0;
      const elev = tileElevationAt(q, r, biomeMap);
      dummy.position.set(x, elev + yOffset, z);
      dummy.rotation.set(0, 0, 0);
      dummy.scale.set(1, 1, 1);
      dummy.updateMatrix();
      mesh.setMatrixAt(localIdx, dummy.matrix);

      if (perOwnerTint) {
        color.copy(
          roadTintFor(
            props?.roadOwner as number | undefined,
            props?.controllerArchetype as string | undefined,
            props?.controllerAlignment as string | undefined,
          ),
        );
        mesh.setColorAt(localIdx, color);
      } else if (pathTint) {
        color.copy(
          bridgeTint(
            props?.controllerArchetype as string | undefined,
            props?.controllerAlignment as string | undefined,
          ),
        );
        mesh.setColorAt(localIdx, color);
      }
    });

    mesh.instanceMatrix.needsUpdate = true;
    if ((perOwnerTint || pathTint) && mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.count = entries.length;
  }, [entries, yOffset, biomeMap, perOwnerTint, pathTint]);

  return (
    <instancedMesh
      ref={meshRef}
      args={[roadGeo, material, entries.length]}
      renderOrder={RenderLayer.Infrastructure}
      frustumCulled={true}
      receiveShadow
    />
  );
}
